import React, { useEffect, useState } from "react";
import PageWrapper, { PageHero } from "../components/PageWrapper";
import LeadForm from "../components/LeadForm";
import api from "../lib/api";
import {
  Globe2, UserPlus, Layers3, Video, Brain, Award, Trophy,
  MessagesSquare, ClipboardList, BarChart3, Users,
} from "lucide-react";

const FEATURES = [
  { icon: Globe2, title: "Online + Offline Hosting", desc: "Run hybrid, in-person, and fully online events from one console." },
  { icon: UserPlus, title: "Delegate Registration", desc: "End-to-end delegate workflows with payments, allocations, and ID badges." },
  { icon: Layers3, title: "Council & Country Allocation", desc: "Automated allocations balanced by preference, performance, and policy." },
  { icon: ClipboardList, title: "Session & Agenda Management", desc: "Live-managed agendas, session moderation, and policy frameworks." },
  { icon: Video, title: "Live Video Integrations", desc: "Native integrations with Zoom, Meet, and broadcast workflows." },
  { icon: MessagesSquare, title: "Real-Time Moderation", desc: "Tools for chairs and secretariats to maintain decorum at scale." },
  { icon: Brain, title: "AI-Based Evaluation", desc: "AI-assisted scoring informed by speech, engagement, and substance." },
  { icon: Award, title: "Digital Certification", desc: "Branded, verifiable certificates generated automatically post-event." },
  { icon: Trophy, title: "Leaderboards & Analytics", desc: "Live leaderboards and post-event analytics for delegations." },
  { icon: BarChart3, title: "Result Analytics", desc: "Institution-level dashboards across multiple events and editions." },
  { icon: Users, title: "Post-Event Engagement", desc: "Alumni networks, follow-on programs, and feedback systems." },
];

const USE_CASES = [
  { title: "Model United Nations", desc: "MUN conferences from school to inter-university to global flagship scale." },
  { title: "Youth Parliaments", desc: "Policy and parliamentary simulations with full delegate workflows." },
  { title: "Academic Conferences", desc: "Multi-track academic conferences with paper management and panels." },
  { title: "Institutional Events", desc: "Strategic institutional events — retreats, summits, and convocations." },
];

export default function EventOSPage() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {});
  }, []);

  return (
    <PageWrapper testId="event-os-page">
      <PageHero
        eyebrow="Event & MUN OS"
        title="The operating system for global academic events."
        description="A complete platform to host, manage, and scale Model United Nations, academic conferences, youth parliaments, and institutional events worldwide."
        image="https://images.pexels.com/photos/20733081/pexels-photo-20733081.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <section className="bg-white py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
              Built for scale
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight">
              From a 200-delegate school MUN to a 10,000-delegate global summit.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3 space-y-5 text-slate-600 leading-relaxed text-base sm:text-lg">
            <p>
              Event OS replaces the operational chaos of running global academic
              events with a single platform. Delegate flows, council
              allocations, session moderation, AI-assisted evaluation, and
              certification — all engineered to compound across editions.
            </p>
            <p>
              Hosting institutions stop firefighting and start building a
              programme legacy.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
            Use Cases
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight max-w-3xl">
            Designed for the academic event categories that matter.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {USE_CASES.map((u, i) => (
              <div
                key={u.title}
                data-testid={`event-usecase-${i}`}
                className="card-lift bg-white border border-slate-200 p-7"
              >
                <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
                  0{i + 1}
                </div>
                <h3 className="mt-2 font-display text-xl text-navy">{u.title}</h3>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                  {u.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
            Capabilities
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight max-w-3xl">
            Everything required to run a world-class academic event.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={`event-feature-${i}`}
                className="card-lift bg-white border border-slate-200 p-7"
              >
                <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center">
                  <f.icon size={18} strokeWidth={1.5} />
                </div>
                <div className="mt-6 font-display text-lg text-navy">{f.title}</div>
                <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {f.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {events.length ? (
        <section className="bg-[#F8FAFC] py-24 sm:py-28">
          <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
              Flagship Editions
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight max-w-3xl">
              Selected editions running on Event OS.
            </h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              {events.map((e) => (
                <div
                  key={e.id}
                  data-testid={`event-card-${e.id}`}
                  className="card-lift bg-white border border-slate-200 overflow-hidden"
                >
                  {e.cover_image ? (
                    <div className="aspect-[4/3] bg-slate-100">
                      <img
                        src={e.cover_image}
                        alt={e.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : null}
                  <div className="p-7">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
                      {e.event_type}
                    </div>
                    <div className="mt-2 font-display text-xl text-navy">{e.title}</div>
                    <div className="mt-2 text-sm text-slate-600">
                      {e.location} · {e.date_label}
                    </div>
                    <div className="mt-4 text-sm text-slate-700 leading-relaxed">
                      {e.summary}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="bg-navy text-white py-24 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-navy opacity-30" />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold">
              Host with us
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light tracking-tight">
              Host your next academic event on Event OS.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              From a flagship MUN to a youth parliament, our platform and
              execution team partner with you end-to-end.
            </p>
          </div>
          <div className="lg:col-span-7">
            <LeadForm
              inquiryType="event"
              variant="dark"
              testIdPrefix="eventos-lead-form"
              title="Request an Event OS consultation"
              subtitle="Tell us about your event. We'll respond within one business day."
            />
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
